import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BellOutlined,
  BranchesOutlined,
  BuildOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DotChartOutlined,
  FileDoneOutlined,
  OrderedListOutlined,
  RiseOutlined,
  SendOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import { ComingSoonFeatureConfig } from 'types/comingSoonFeatures';

export const COMING_SOON_FEATURE = {
  SPEED_TO_LEAD: 'speed_to_lead',
  SALES_EXPERIENCE: 'sales_experience',
  EMAIL_MARKETING: 'email_marketing',
  CONSTRUCTION_RENOVATION: 'construction_renovation',
} as const;

export type ComingSoonFeatureKey = (typeof COMING_SOON_FEATURE)[keyof typeof COMING_SOON_FEATURE];

export const COMING_SOON_ROUTE = '/ai-competitive-intelligence/coming-soon';

export const MAX_PILOT_COMMUNITIES = 2;

export const COMING_SOON_FEATURES: ComingSoonFeatureConfig[] = [
  {
    key: COMING_SOON_FEATURE.SPEED_TO_LEAD,
    label: 'Speed to Lead',
    isPilot: true,
    iconClassName: 'standalone',
    iconImageSrc: '/icons/speed-to-lead.svg',
    overviewDescription: 'How fast every community in your markets responds to a new inquiry - and where you rank.',
    titlePrefix: 'Track',
    titleHighlight: 'Speed to Lead',
    description:
      'When a family inquires, they inquire everywhere — and most choose whoever answers first. We submit ordinary ' +
      'website inquiries to you and every competitor, and AI measures what happens next: how fast each community ' +
      'responds, on which channels, and how persistently they follow up — then shows you exactly where the lead is ' +
      'won or lost, with action plans to win it.',
    stats: [
      {
        value: '75',
        unit: '%',
        title: 'Of families choose the first responder',
        text: 'Most prospects pick the first community that gets back to them. Being second means being invisible.',
        source: 'Senior living industry studies',
      },
      {
        value: '21',
        unit: 'hrs',
        title: 'The industry’s average first response',
        text: 'That’s how long the average community keeps a family waiting. Find out if your market is faster and if you are.',
        source: 'Bild & Co. senior living benchmarks',
      },
      {
        value: '21',
        unit: 'x',
        title: 'More likely to qualify',
        text: 'A lead contacted within 5 minutes is 21× more likely to qualify than one contacted at 30. Every minute is occupancy.',
        source: 'Lead Response Management study',
      },
    ],
    steps: ['We inquire like a family', 'AI times and scores every response', 'You get rankings, gaps, and plans'],
    benefits: [
      {
        icon: OrderedListOutlined,
        title: 'Your Market Ranking',
        text: 'Where you stand - overall, on speed, and on quality - against every competitor, scored 0-100.',
      },
      {
        icon: DotChartOutlined,
        title: 'Quality vs. Speed Map',
        text: 'One picture of the whole market: who’s fast, who’s good, and who’s both. Top right wins.',
      },
      {
        icon: RiseOutlined,
        title: '7-Day Follow-Up Timelines',
        text: 'Every competitor touch - email, call, voicemail, SMS - day by day, next to yours. See exactly when they give up and when you do.',
      },
      {
        icon: CheckSquareOutlined,
        title: 'AI Action Plans',
        text: 'Every gap becomes a ready-to-run play for your team - what to change, and why it wins the lead.',
      },
    ],
    preview: {
      label: 'Speed to Lead',
      value: '4th',
      suffix: 'of 5 communities',
      barClassName: 'gold',
      cardTitle: 'First Response',
      cardText: 'Your auto-reply landed in 0 min — fastest in the market',
      cardMeta: 'Email · 0 min',
      metaClassName: 'blue',
    },
    notePlaceholder:
      'e.g. We think competitors respond faster on weekends, or we’re losing tours to a new community nearby...',
  },
  {
    key: COMING_SOON_FEATURE.SALES_EXPERIENCE,
    label: 'Sales Experience',
    isPilot: false,
    iconClassName: 'blue',
    iconImageSrc: '/icons/support-agent.svg',
    overviewDescription:
      'What families hear when they call your competitors - call quality, follow-up, and promises kept.',
    titlePrefix: 'Measure the',
    titleHighlight: 'Sales Experience',
    description:
      'Hear what families actually experience when they call your competitors. AI-scored shopper calls grade every ' +
      'community on greeting, discovery, empathy, and pricing conversations — then track what happens after the ' +
      'call: whether reps follow up over phone, email, and SMS, and whether they keep the promises they made.',
    stats: [
      {
        value: '8',
        unit: '',
        title: 'Attempts to reach a decision maker',
        text: 'It takes an average of eight call attempts to get a prospect on the phone. We count how many it takes to reach each community’s sales director — past the front desk.',
        source: 'TOPO sales research',
      },
      {
        value: '72',
        unit: '%',
        title: 'Switch after one bad experience',
        text: 'Nearly three in four consumers are likely to move to a competitor after a single bad experience. Once connected, AI scores the conversation itself - greeting, discovery, empathy, pricing.',
        source: 'Northridge Group study',
      },
      {
        value: '44',
        unit: '%',
        title: 'Of reps give up after one follow-up',
        text: 'Nearly half of salespeople stop after a single attempt. We track every post-call touch — and whether the promises made on the call were kept.',
        source: 'Cross-industry sales research',
      },
    ],
    steps: ['We call until we reach sales', 'AI scores the call and follow-ups', 'You get scores, gaps, and plans'],
    benefits: [
      {
        icon: FileDoneOutlined,
        title: 'Conversation Scoring',
        text: 'Greeting, discovery, empathy, and pricing conversations — scored on every call.',
      },
      {
        icon: SendOutlined,
        title: 'Post-Call Follow-Up',
        text: 'Which channels fire after the call — phone, email, SMS — how fast, and when they go quiet.',
      },
      {
        icon: AuditOutlined,
        title: 'Promises Kept',
        text: 'Every commitment a rep makes is tracked to see if it actually arrived.',
      },
      {
        icon: CheckSquareOutlined,
        title: 'AI Action Plans',
        text: 'Call scores become coaching plans - what your reps should ask, say, and fix on the next call.',
      },
    ],
    preview: {
      label: 'Call Score',
      value: '76',
      suffix: '/100 · 2nd of 5',
      barClassName: 'green',
      cardTitle: 'Promise Kept?',
      cardText: '“I’ll send pricing this afternoon” — arrived 3 days later',
      cardMeta: 'Missed · 3 days',
      metaClassName: 'orange',
    },
    notePlaceholder:
      'e.g. We want to know what competitors promise families on the phone, or how quickly a family can reach their sales team...',
  },
  {
    key: COMING_SOON_FEATURE.EMAIL_MARKETING,
    label: 'Email Marketing',
    isPilot: false,
    iconClassName: 'pink',
    iconImageSrc: '/icons/email-pink.svg',
    overviewDescription: 'The emails competitors send to prospects - cadence, topics, and the gaps in your program.',
    titlePrefix: 'Decode',
    titleHighlight: 'Email Marketing',
    description:
      'When a family inquires, competitor emails start arriving — and keep arriving for months. We inquire like a ' +
      'prospect and monitor the inbox as emails come in. AI categorizes each send, shows how often every community ' +
      'shows up and with what story, and exposes the topics you’re missing.',
    stats: [
      {
        value: '70-120',
        unit: '',
        title: 'Days in the senior living sales cycle',
        text: 'Families research for months, and competitor emails keep arriving the whole time. The inbox timeline shows who stays present — and who goes quiet.',
        source: 'Industry benchmark data',
      },
      {
        value: '3',
        unit: '+',
        title: 'Communities on every shortlist',
        text: 'Families compare several communities at once — and every competitor email shapes the story they hear. Know what you’re up against, send by send.',
        source: 'Senior living industry research',
      },
      {
        value: '76',
        unit: '%',
        title: 'Tune out irrelevant content',
        text: 'Prospects disengage when messages don’t speak to what they care about. Topic coverage shows the questions competitors answer that you don’t.',
        source: 'McKinsey personalization research',
      },
    ],
    steps: [
      'We inquire and watch the inbox',
      'AI categorizes each email as it arrives',
      'You get cadence, topics, and gaps',
    ],
    benefits: [
      {
        icon: AppstoreOutlined,
        title: 'Topic Coverage & Gaps',
        text: 'What every competitor is saying - and the topics they own that you haven’t touched yet.',
      },
      {
        icon: BranchesOutlined,
        title: 'Prospect Inbox Timeline',
        text: 'The cadence a family actually experiences from each community - who keeps showing up, and whose nurture goes quiet.',
      },
      {
        icon: BellOutlined,
        title: 'Promises Kept',
        text: 'Get flagged when a competitor ramps up volume or launches a promotion - so you can respond before it costs you move-ins.',
      },
      {
        icon: CheckSquareOutlined,
        title: 'AI Action Plans',
        text: 'AI-generated recommendations to strengthen your email program against the market.',
      },
    ],
    preview: {
      label: 'Category Share',
      value: '0%',
      suffix: 'Dining — market gap',
      barClassName: 'green',
      cardTitle: 'Cedar Ridge Estates',
      cardText: '“Fall Harvest Dinner — save your seat!”',
      cardMeta: 'Events · Week 8',
      metaClassName: 'orange',
    },
    notePlaceholder:
      'e.g. We want to know which campaigns competitors run, or why families mention offers we never sent...',
  },
  {
    key: COMING_SOON_FEATURE.CONSTRUCTION_RENOVATION,
    label: 'Construction & Renovation',
    isPilot: false,
    iconClassName: 'gold',
    iconImageSrc: '/icons/construction.svg',
    overviewDescription: 'New builds, expansions, and renovations in your markets - caught while still in planning.',
    titlePrefix: 'Track',
    titleHighlight: 'Construction & Renovation',
    description:
      'Nearly half the buildings in this industry are old enough to need work - and when the one down the road gets ' +
      'it, your community is what looks dated. A competitor who builds, adds units or renovates comes back with a ' +
      'better tour, a higher rate and a marketing campaign aimed at the same families your team is working. We ' +
      'watch construction activity across your markets and notify you while a project is still in planning, a year ' +
      'or more before it opens.',
    stats: [
      {
        value: '45',
        unit: '%',
        title: 'Of communities are 25 years or older',
        text: 'Almost half the industry’s buildings are due for work. The question isn’t whether a competitor near you renovates - it’s whether your team finds out before a family does.',
        source: 'Senior housing inventory analysis',
      },
      {
        value: '2-3',
        unit: 'yrs',
        title: 'From planning to opening day',
        text: 'A senior living project spends years in planning, permits, and construction before it opens. That window is your head start — but only if you know the project exists.',
        source: 'Development timeline benchmarks',
      },
      {
        value: '2.4',
        unit: 'pts',
        title: 'The census gap by building age',
        text: 'Communities 25 years and older sit at 84.4% occupancy. Buildings 10 to 17 years old sit at 86.8%. Age shows up in census - so when a competitor resets theirs, the move-ins come from somewhere.',
        source: 'Occupancy analysis by property age',
      },
    ],
    steps: [
      'We watch construction activity in your markets',
      'AI matches each project to your comp set',
      'You get notified while it’s still on paper',
    ],
    benefits: [
      {
        icon: BankOutlined,
        title: 'New Communities Being Built',
        text: 'A new building near you, caught at planning or bidding - long before its pre-leasing campaign reaches your leads.',
      },
      {
        icon: BuildOutlined,
        title: 'Expansions',
        text: 'Units added to a community already in your comp set - no new address on the map to warn you, so it’s the one teams miss.',
      },
      {
        icon: ToolOutlined,
        title: 'Renovations and Remodels',
        text: 'A competitor reopening with a stronger tour and a higher rate resetting what your prospects compare you against',
      },
      {
        icon: ClockCircleOutlined,
        title: 'Time to Get Ahead of It',
        text: 'Every project comes with its stage and value - a year or more to prepare your team, your rate, and your story.',
      },
    ],
    preview: {
      label: 'Project Value',
      value: '$19.4M',
      suffix: 'in final plan',
      barClassName: 'green',
      cardTitle: 'Cedar Ridge Estates',
      cardText: 'Expansion - in final planning, reopening 2027',
      cardMeta: 'Detected • 6.2 mi away',
      metaClassName: 'orange',
    },
    notePlaceholder:
      'e.g. We heard a project is planned nearby, or we want early warning before a competitor renovates...',
  },
];
